/* ──────────────────────────────────────────────────────────────────────────
   ChangeHistoryDrawer — kayıt DEĞİŞİKLİK GEÇMİŞİ çekmecesi (drawer).
   Detay ekranında geçmiş (⟳) butonuna tıklanınca sağdan açılır; kayıt üzerinde
   yapılan oluşturma/güncelleme/silme işlemlerini bir ZAMAN ÇİZELGESİ (timeline)
   olarak listeler. Güncellemeler alan bazlı diff gösterir: eski değer → yeni değer.
   Veri: GET /api/ChangeHistory?entityName=&entityId= (merkezî; tüm ekranlar için tek uç).
   Kullanıcı adları auditUsers resolver'ı ile çözülür (audit info kartıyla aynı kaynak).
   ────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useRef } from 'react';
import { authHeaders } from '../listPageStyles';
import { loadAuditUserMap, resolveAuditUser } from './auditUsers';
import { Icon } from './DetailShell';

function unwrapArr(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function fmtDateTime(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function parseChanges(raw) {
  if (!raw) return [];
  try {
    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

const ACTION_META = {
  Create: { label: 'Oluşturuldu', cls: 'create' },
  Update: { label: 'Güncellendi', cls: 'update' },
  Delete: { label: 'Silindi', cls: 'delete' },
};

function EmptyVal({ children }) {
  const v = children === null || children === undefined || children === '' ? '—' : children;
  return <span className="dps-hist-val">{v}</span>;
}

function ChangeRow({ change, action }) {
  const label = change.field ?? change.label;
  const oldV = change.oldValue ?? change.OldValue;
  const newV = change.newValue ?? change.NewValue;
  return (
    <div className="dps-hist-change">
      <span className="dps-hist-field">{label}</span>
      <div className="dps-hist-diff">
        {action !== 'Create' && (
          <>
            <span className="dps-hist-old"><EmptyVal>{oldV}</EmptyVal></span>
            <span className="dps-hist-arrow"><Icon name="arrowRight" size={14} /></span>
          </>
        )}
        <span className={action === 'Delete' ? 'dps-hist-removed' : 'dps-hist-new'}>
          <EmptyVal>{action === 'Delete' ? '—' : newV}</EmptyVal>
        </span>
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

export function ChangeHistoryDrawer({ open, onClose, entityName, entityId, title = 'Değişiklik Geçmişi', audit }) {
  const [items, setItems] = useState(null);   // birikimli (accumulated) sayfalar
  const [loading, setLoading] = useState(false);     // ilk yükleme
  const [loadingMore, setLoadingMore] = useState(false); // sonraki sayfa
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [userMap, setUserMap] = useState(null);
  const fetchingRef = useRef(false); // çift tetiklemeyi engelle (scroll yarışları)

  // Bir sayfa çek. reset=true → baştan (skip 0), aksi halde mevcut sayının ardına ekle.
  const loadPage = useCallback(async (reset, currentCount) => {
    if (!entityName || !entityId) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    const skip = reset ? 0 : currentCount;
    if (reset) { setLoading(true); setError(null); } else { setLoadingMore(true); }
    try {
      const [res, map] = await Promise.all([
        fetch(`/api/ChangeHistory?entityName=${encodeURIComponent(entityName)}&entityId=${encodeURIComponent(entityId)}&skip=${skip}&take=${PAGE_SIZE}`, { headers: authHeaders() }),
        loadAuditUserMap(),
      ]);
      setUserMap(map);
      if (!res.ok) throw new Error(`Geçmiş alınamadı (${res.status})`);
      const page = unwrapArr(await res.json());
      setHasMore(page.length === PAGE_SIZE);
      setItems((prev) => (reset || !prev ? page : [...prev, ...page]));
    } catch (err) {
      if (reset) { setError(err.message || 'Geçmiş alınamadı'); setItems([]); }
      setHasMore(false);
    } finally {
      setLoading(false); setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [entityName, entityId]);

  // Çekmece her açıldığında baştan yükle.
  useEffect(() => { if (open) loadPage(true, 0); }, [open, loadPage]);

  // Aşağı kaydırınca sonraki sayfayı iste (sonsuz kaydırma).
  const handleScroll = (e) => {
    if (!hasMore || loadingMore || loading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
      loadPage(false, items ? items.length : 0);
    }
  };

  // Esc ile kapat.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // "Oluşturuldu" düğümü audit'ten sentezlenir (backend loglamaz). Pascal/camel iki adı da oku.
  const createdBy = audit && (audit.createdBy ?? audit.CreatedBy);
  const createdDate = audit && (audit.createdDate ?? audit.CreatedDate);

  return (
    <div className="dps-hist-overlay" onClick={onClose}>
      <aside className="dps-hist-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <header className="dps-hist-head">
          <div className="dps-hist-head-tt">
            <Icon name="history" size={18} />
            <h3>{title}</h3>
          </div>
          <button className="dps-hist-x" onClick={onClose} aria-label="Kapat">×</button>
        </header>

        <div className="dps-hist-body" onScroll={handleScroll}>
          {loading && <div className="dps-hist-state">Yükleniyor…</div>}
          {!loading && error && <div className="dps-hist-state err">{error}</div>}
          {/* Boş durum: hiç güncelleme/silme kaydı YOK ve oluşturulma bilgisi de yoksa. */}
          {!loading && !error && items && items.length === 0 && !createdDate && (
            <div className="dps-hist-state">
              <Icon name="clock" size={28} />
              <p>Bu kayıt için henüz değişiklik kaydı yok.</p>
            </div>
          )}

          {!loading && !error && items && (items.length > 0 || createdDate) && (
            <ol className="dps-hist-timeline">
              {items.map((it) => {
                const action = it.action ?? it.Action;
                const meta = ACTION_META[action] || { label: action, cls: 'update' };
                const changes = parseChanges(it.changesJson ?? it.ChangesJson);
                const who = resolveAuditUser(it.changedBy ?? it.ChangedBy, userMap);
                const when = fmtDateTime(it.changedDate ?? it.ChangedDate);
                return (
                  <li key={it.id ?? it.Id} className="dps-hist-node">
                    <span className={`dps-hist-dot ${meta.cls}`} />
                    <div className="dps-hist-card">
                      <div className="dps-hist-meta">
                        <span className={`dps-hist-badge ${meta.cls}`}>{meta.label}</span>
                        <span className="dps-hist-when">{when}</span>
                      </div>
                      <div className="dps-hist-who"><Icon name="user" size={13} /> {who}</div>
                      {changes.length > 0 ? (
                        <div className="dps-hist-changes">
                          {changes.map((c, i) => <ChangeRow key={i} change={c} action={action} />)}
                        </div>
                      ) : (
                        <div className="dps-hist-nochange">{meta.label}</div>
                      )}
                    </div>
                  </li>
                );
              })}

              {/* "Oluşturuldu" düğümü backend'de LOGLANMAZ — audit'ten (CreatedBy/CreatedDate)
                  sentezlenir ve zaman çizelgesinin EN ALTINA (en eski) tüm sayfalar yüklendiğinde eklenir. */}
              {!hasMore && createdDate && (
                <li key="__created__" className="dps-hist-node">
                  <span className="dps-hist-dot create" />
                  <div className="dps-hist-card">
                    <div className="dps-hist-meta">
                      <span className="dps-hist-badge create">Oluşturuldu</span>
                      <span className="dps-hist-when">{fmtDateTime(createdDate)}</span>
                    </div>
                    <div className="dps-hist-who"><Icon name="user" size={13} /> {resolveAuditUser(createdBy, userMap)}</div>
                    <div className="dps-hist-nochange">Kayıt oluşturuldu</div>
                  </div>
                </li>
              )}
            </ol>
          )}

          {loadingMore && <div className="dps-hist-more">Daha fazla yükleniyor…</div>}
        </div>
      </aside>
    </div>
  );
}

export default ChangeHistoryDrawer;
